import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FirebaseContext } from "../contexts/FirebaseContext";
import TopBar from "../components/TopBar";
import { ReactComponent as BackIcon } from "../assets/icon_back.svg";
import Colors from "../constants/Colors";
import { buildInvoicePdfData } from "../utils/invoiceData";
import { generateInvoicePdf } from "../utils/invoicePdf";
import { getCurrencyInfo } from "../utils/billingRateSetting";
import { msToDateInput, dateInputToMs } from "../utils/invoiceDates";

function InvoicePreviewScreen() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { invoices, clients, projects, getInvoiceSourceData } = useContext(FirebaseContext);
  const invoice = invoices.find((i) => String(i.id) === String(invoiceId)) || null;
  const currency = getCurrencyInfo();

  // Load whole-account tasks + time records (line items can span projects).
  const [source, setSource] = useState(null);
  useEffect(() => {
    let alive = true;
    getInvoiceSourceData().then((s) => { if (alive) setSource(s); });
    return () => { alive = false; };
  }, [getInvoiceSourceData]);

  // Seed the editable form once from the stored invoice + computed line items.
  const seed = useMemo(
    () =>
      invoice && source
        ? buildInvoicePdfData(invoice, {
            clients,
            tasks: source.tasks,
            projects,
            timeRecords: source.timeRecords,
          })
        : null,
    // Seed only on first load of this invoice; edits are session-local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoice && invoice.id, source]
  );

  const [form, setForm] = useState(null);
  // Initialize form state from the seed the first time it's available.
  if (seed && !form) {
    setForm({
      senderName: seed.sender.name,
      senderAddress: seed.sender.address,
      senderPhone: seed.sender.phone,
      bankSwift: seed.sender.bankSwift,
      bankName: seed.sender.bankName,
      bankAddress: seed.sender.bankAddress,
      accountNumber: seed.sender.accountNumber,
      beneficiaryName: seed.sender.beneficiaryName,
      clientName: seed.client.name,
      clientAddress: seed.client.address,
      clientTaxId: seed.client.taxId,
      invoiceNumber: String(seed.invoiceNumber),
      issueDate: seed.issueDate,
      dueDate: seed.dueDate,
      taxRatePercent: String(seed.taxRatePercent),
      notes: seed.notes,
      lineItems: seed.lineItems.map((it) => ({
        description: it.description,
        quantityHours: String(Number(it.quantityHours).toFixed(2)),
        unitPrice: String(it.unitPrice),
      })),
    });
  }

  if (!invoice || !form) {
    return (
      <div style={styles.container}>
        <TopBar
          leftContainer={
            <button onClick={() => navigate(-1)} style={styles.backBtn} aria-label="Back">
              <BackIcon style={{ width: 22, height: 22, fill: "var(--text)" }} />
            </button>
          }
          centerContainer={<span style={styles.headerTitle}>Preview</span>}
        />
        <div style={styles.body}><div style={{ color: "var(--text-muted)" }}>Loading…</div></div>
      </div>
    );
  }

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setItem = (i, patch) =>
    setForm((f) => ({ ...f, lineItems: f.lineItems.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));
  const addItem = () =>
    setForm((f) => ({ ...f, lineItems: [...f.lineItems, { description: "", quantityHours: "0", unitPrice: "0" }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, lineItems: f.lineItems.filter((_, j) => j !== i) }));

  const totals = () => {
    const subtotal = form.lineItems.reduce(
      (s, it) => s + (parseFloat(it.quantityHours) || 0) * (parseFloat(it.unitPrice) || 0),
      0
    );
    const tax = subtotal * ((parseFloat(form.taxRatePercent) || 0) / 100);
    return { subtotal, tax, due: subtotal + tax };
  };

  const handleGenerate = () => {
    generateInvoicePdf({
      sender: {
        name: form.senderName,
        address: form.senderAddress,
        phone: form.senderPhone,
        bankSwift: form.bankSwift,
        bankName: form.bankName,
        bankAddress: form.bankAddress,
        accountNumber: form.accountNumber,
        beneficiaryName: form.beneficiaryName,
      },
      client: { name: form.clientName, address: form.clientAddress, taxId: form.clientTaxId },
      invoiceNumber: form.invoiceNumber,
      issueDate: form.issueDate,
      dueDate: form.dueDate,
      lineItems: form.lineItems.map((it) => ({
        description: it.description,
        quantityHours: parseFloat(it.quantityHours) || 0,
        unitPrice: parseFloat(it.unitPrice) || 0,
      })),
      taxRatePercent: parseFloat(form.taxRatePercent) || 0,
      notes: form.notes,
      currency: { symbol: currency.symbol, code: currency.code },
    });
  };

  const t = totals();
  const fmt = (n) => `${currency.symbol}${n.toFixed(2)} ${currency.code}`;

  return (
    <div style={styles.container}>
      <TopBar
        leftContainer={
          <button onClick={() => navigate(-1)} style={styles.backBtn} aria-label="Back">
            <BackIcon style={{ width: 22, height: 22, fill: "var(--text)" }} />
          </button>
        }
        centerContainer={<span style={styles.headerTitle}>Preview</span>}
      />
      <div style={styles.body}>
        <div style={styles.section}>FROM</div>
        <input style={styles.input} placeholder="Your name / business" value={form.senderName} onChange={(e) => set({ senderName: e.target.value })} />
        <input style={styles.input} placeholder="Address" value={form.senderAddress} onChange={(e) => set({ senderAddress: e.target.value })} />
        <input style={styles.input} placeholder="Phone" value={form.senderPhone} onChange={(e) => set({ senderPhone: e.target.value })} />

        <div style={styles.section}>BILL TO</div>
        <input style={styles.input} placeholder="Client name" value={form.clientName} onChange={(e) => set({ clientName: e.target.value })} />
        <input style={styles.input} placeholder="Client address" value={form.clientAddress} onChange={(e) => set({ clientAddress: e.target.value })} />
        <input style={styles.input} placeholder="Tax ID" value={form.clientTaxId} onChange={(e) => set({ clientTaxId: e.target.value })} />

        <div style={styles.section}>INVOICE</div>
        <label style={styles.label}>Invoice number</label>
        <input style={styles.input} value={form.invoiceNumber} onChange={(e) => set({ invoiceNumber: e.target.value })} />
        <div style={styles.dateGrid}>
          <div>
            <label style={styles.label}>Issue date</label>
            <input style={styles.input} type="date" value={msToDateInput(form.issueDate)} onChange={(e) => set({ issueDate: dateInputToMs(e.target.value) })} />
          </div>
          <div>
            <label style={styles.label}>Due date</label>
            <input style={styles.input} type="date" value={msToDateInput(form.dueDate)} onChange={(e) => set({ dueDate: dateInputToMs(e.target.value) })} />
          </div>
        </div>

        <div style={styles.section}>LINE ITEMS</div>
        {form.lineItems.map((it, i) => (
          <div key={i} style={styles.itemRow}>
            <input style={{ ...styles.input, flex: 2 }} placeholder="Description" value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} />
            <input style={{ ...styles.input, flex: 1 }} placeholder="Hrs" inputMode="decimal" value={it.quantityHours} onChange={(e) => setItem(i, { quantityHours: e.target.value.replace(/[^0-9.]/g, "") })} />
            <input style={{ ...styles.input, flex: 1 }} placeholder="Rate" inputMode="decimal" value={it.unitPrice} onChange={(e) => setItem(i, { unitPrice: e.target.value.replace(/[^0-9.]/g, "") })} />
            <button style={styles.removeBtn} onClick={() => removeItem(i)} aria-label="Remove">✕</button>
          </div>
        ))}
        <button style={styles.addBtn} onClick={addItem}>+ Add line item</button>

        <label style={styles.label}>Tax rate (%)</label>
        <input style={styles.input} inputMode="decimal" value={form.taxRatePercent} onChange={(e) => set({ taxRatePercent: e.target.value.replace(/[^0-9.]/g, "") })} />

        <div style={styles.totals}>
          <div style={styles.totalLine}><span>Subtotal</span><span>{fmt(t.subtotal)}</span></div>
          <div style={styles.totalLine}><span>Tax</span><span>{fmt(t.tax)}</span></div>
          <div style={{ ...styles.totalLine, fontWeight: 700 }}><span>Amount due</span><span>{fmt(t.due)}</span></div>
        </div>

        <div style={styles.section}>NOTES</div>
        <textarea style={{ ...styles.input, minHeight: "70px", resize: "vertical" }} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />

        <div style={styles.section}>BANK</div>
        <input style={styles.input} placeholder="Bank SWIFT / Routing" value={form.bankSwift} onChange={(e) => set({ bankSwift: e.target.value })} />
        <input style={styles.input} placeholder="Bank name" value={form.bankName} onChange={(e) => set({ bankName: e.target.value })} />
        <input style={styles.input} placeholder="Bank address" value={form.bankAddress} onChange={(e) => set({ bankAddress: e.target.value })} />
        <input style={styles.input} placeholder="Account number" value={form.accountNumber} onChange={(e) => set({ accountNumber: e.target.value })} />
        <input style={styles.input} placeholder="Beneficiary name" value={form.beneficiaryName} onChange={(e) => set({ beneficiaryName: e.target.value })} />
      </div>

      <div style={styles.ctaBar}>
        <button style={styles.primaryCta} onClick={handleGenerate}>Generate PDF</button>
      </div>
    </div>
  );
}

const styles = {
  container: { paddingTop: "50px", height: "calc(100vh - 60px)", boxSizing: "border-box" },
  backBtn: { background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", padding: 0 },
  headerTitle: { fontWeight: 700 },
  body: { padding: "16px", paddingBottom: "90px", maxWidth: "620px", margin: "0 auto", height: "100%", overflowY: "auto", boxSizing: "border-box" },
  section: { fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", letterSpacing: "0.5px", margin: "18px 0 8px" },
  label: { fontSize: "12px", color: "var(--text-muted)", margin: "8px 0 4px", display: "block" },
  input: { padding: "10px", borderRadius: "8px", border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", fontSize: "14px", width: "100%", boxSizing: "border-box", marginBottom: "8px" },
  dateGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" },
  itemRow: { display: "flex", gap: "6px", alignItems: "center" },
  removeBtn: { background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "16px", padding: "0 6px" },
  addBtn: { background: "var(--surface)", border: "1px dashed var(--border)", color: "var(--text)", borderRadius: "8px", padding: "10px", cursor: "pointer", width: "100%", marginBottom: "8px" },
  totals: { borderTop: "1px solid var(--border)", marginTop: "8px", paddingTop: "8px" },
  totalLine: { display: "flex", justifyContent: "space-between", color: "var(--text)", fontSize: "14px", padding: "3px 0" },
  ctaBar: { position: "fixed", left: 0, right: 0, bottom: "60px", padding: "12px 16px", background: "var(--bg)", borderTop: "1px solid var(--border)", boxSizing: "border-box" },
  primaryCta: { width: "100%", padding: "12px", background: Colors.primary, color: "#fff", border: "none", borderRadius: "10px", fontWeight: 700, cursor: "pointer" },
};

export default InvoicePreviewScreen;
